import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Whether preview panels are in the units isolation rollout.
 *
 * Two gates drive the same rollout so it can be turned on for either an organisation or a single
 * site: `preview_panel_unit_check` targets org id, `preview_panel_unit_check_cloud_id` targets
 * cloud id. Either one being on opts preview panels in, and both are always evaluated so that
 * exposure is recorded for the two of them.
 *
 * Being in the rollout is not on its own enough to change behaviour - the organisation also has to
 * have launched units with boundary enforcement on. That part of the decision is owned by
 * `shouldUseUnitCompliantApi` from `@atlaskit/linking-common/units-rollout`, which this is passed
 * to (see `useUnitIsolationEnabled`), rather than being decided here.
 */
export const isPreviewPanelInUnitsRollout = (): boolean => {
	const isOrgInRollout = fg('preview_panel_unit_check');
	const isSiteInRollout = fg('preview_panel_unit_check_cloud_id');

	return isOrgInRollout || isSiteInRollout;
};

/**
 * The synchronous half of the units isolation decision, used to choose between the cross-unit
 * aware implementation of `useGlobalObjectPreviewPanels` and the legacy one.
 *
 * `cc-units-ga` - the units GA master gate owned by the units team - is the killswitch for every
 * product's units isolation behaviour and is deliberately checked first: while it is off, no
 * preview panel gate is evaluated, so we do not record exposure for users who could not have
 * received the behaviour anyway.
 *
 * Passing this is necessary but not sufficient for a cross-unit resource to actually be blocked:
 * the organisation's own units state (`endUsersLaunched && boundaryEnforced`) is read from AGG at
 * runtime and is applied inside the hook - see `useUnitIsolationEnabled`. Keeping the two apart
 * lets the implementation swap stay synchronous (React requires a stable hook), while the part
 * that needs a network round trip settles afterwards.
 */
export const isPreviewPanelUnitCheckEnabled = (): boolean => {
	// 1. Units GA killswitch.
	if (!fg('cc-units-ga')) {
		return false;
	}

	// 2. Preview panels' own rollout gates.
	return isPreviewPanelInUnitsRollout();
};
